import React from "react";
import Button from "../ui/Button";
import SearchableCountryCode from "./SearchableCountryCode";
import { COUNTRY_CODES } from "../../utils/constants/contact/countryCodes";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { submitContactForm } from "../../features/contact/thunks/contact.thunk";
import { resetContactState } from "../../features/contact/slices/contact.slice";
import {
  validateContact,
  ContactErrors,
} from "../../features/contact/utils/validateContact";

const INTENT_OPTIONS = [
  "Book a Consultation",
  "Request a Demonstration",
  "Discuss a Project",
  "Partnership Enquiry",
  "Follow Port2Market",
  "Interested in AfrikBooking",
  "Careers",
] as const;

const SUBJECT_OPTIONS = [...INTENT_OPTIONS, "Other"] as const;

type FormFieldProps = {
  label: string;
  type?: "text" | "email" | "tel";
  placeholder: string;
  multiline?: boolean;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  error?: string;
};

const FormField: React.FC<FormFieldProps> = ({
  label,
  type = "text",
  placeholder,
  multiline = false,
  name,
  value,
  onChange,
  error,
}) => (
  <div>
    <label className="mb-1 block font-title text-[13px] font-medium text-[#212121]">
      {label}
    </label>
    {multiline ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        className={`w-full rounded-[8px] border bg-white px-3 py-2.5 font-description text-[14px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    ) : (
      <input
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        className={`h-11 w-full rounded-[8px] border bg-white px-3 font-description text-[14px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    )}
    {error && (
      <p className="mt-1 font-description text-[12px] text-red-500">{error}</p>
    )}
  </div>
);

type SelectFieldProps = {
  label: string;
  name: string;
  value: string;
  options: readonly string[];
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  error?: string;
};

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  name,
  value,
  options,
  placeholder = "Select an option",
  onChange,
  error,
}) => (
  <div>
    <label className="mb-1 block font-title text-[13px] font-medium text-[#212121]">
      {label}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className={`h-11 w-full rounded-[8px] border bg-white px-3 font-description text-[14px] font-light text-[#212121] focus:outline-none ${
        !value ? "text-[#D9D9D9]" : ""
      } ${error ? "border-red-300" : "border-[#E4E4E4]"}`}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    {error && (
      <p className="mt-1 font-description text-[12px] text-red-500">{error}</p>
    )}
  </div>
);

type PhoneFieldProps = {
  label: string;
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (value: string) => void;
  error?: string;
};

const PhoneField: React.FC<PhoneFieldProps> = ({
  label,
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error,
}) => {
  const selectedCountry =
    COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw.length <= selectedCountry.digits) {
      onPhoneNumberChange(raw);
    }
  };

  return (
    <div>
      <label className="mb-1 block font-title text-[13px] font-medium text-[#212121]">
        {label}
      </label>
      <div className="flex gap-2">
        <div className="w-[200px] shrink-0 max-[480px]:w-[150px]">
          <SearchableCountryCode
            value={countryCode}
            onChange={onCountryCodeChange}
          />
        </div>
        <input
          type="tel"
          value={phoneNumber}
          onChange={handleNumberChange}
          placeholder={selectedCountry.placeholder}
          className={`h-11 min-w-0 w-full rounded-[8px] border bg-white px-3 font-description text-[14px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
            error ? "border-red-300" : "border-[#E4E4E4]"
          }`}
        />
      </div>
      {error && (
        <p className="mt-1 font-description text-[12px] text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

const ContactForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    isSubmitting,
    isSubmitted,
    error: submitError,
  } = useAppSelector((state) => state.contact);

  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [countryCode, setCountryCode] = React.useState("+237");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [consentError, setConsentError] = React.useState("");
  const [errors, setErrors] = React.useState<ContactErrors>({});

  const buildFullPhone = (code: string, number: string) => {
    if (!number) return "";
    return `${code} ${number}`;
  };

  React.useEffect(() => {
    setForm((p) => ({ ...p, phone: buildFullPhone(countryCode, phoneNumber) }));
  }, [countryCode, phoneNumber]);

  React.useEffect(() => {
    return () => {
      dispatch(resetContactState());
    };
  }, [dispatch]);

  const onChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name as keyof ContactErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const onConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked);
    if (e.target.checked) {
      setConsentError("");
    }
  };

  const onIntentClick = (intent: string) => {
    setForm((p) => ({ ...p, subject: intent }));
    if (errors.subject) {
      setErrors((prev) => ({ ...prev, subject: undefined }));
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!consent) {
      setConsentError(
        "You must agree to the Privacy Policy to submit this form.",
      );
      return;
    }
    setConsentError("");

    const validationErrors = validateContact(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const result = await dispatch(
      submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      }),
    );

    if (submitContactForm.fulfilled.match(result)) {
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setCountryCode("+237");
      setPhoneNumber("");
      setConsent(false);
    }
  };

  return (
    <section
      className="relative w-full px-4 pt-16 pb-24"
      style={{ marginTop: "-60px", zIndex: 10 }}
    >
      <div className="relative mx-auto w-full max-w-[720px] rounded-[30px] border border-[#E1E1E1] bg-white px-8 py-10 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[760px]:px-5 max-[760px]:py-8">
        <div className="text-center">
          <h2 className="font-title text-[30px] font-medium leading-[1.08] text-[#212121] max-[700px]:text-[26px]">
            How can we help?
          </h2>
          <p className="mx-auto mt-2 max-w-[420px] font-description text-[14px] font-light leading-[1.45] text-[#919191]">
            Select a reason for getting in touch, or fill out the form below.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {INTENT_OPTIONS.map((intent) => (
            <Button
              key={intent}
              type="button"
              onClick={() => onIntentClick(intent)}
              className={`rounded-full border px-4 py-2 font-title text-[13px] font-medium transition-colors ${
                form.subject === intent
                  ? "border-[#2776EA] bg-[#F0F7FF] text-[#2776EA]"
                  : "border-[#E4E4E4] bg-white text-[#616161] hover:border-[#B0B0B0]"
              }`}
            >
              {intent}
            </Button>
          ))}
        </div>

        <div className="my-8 h-px bg-[#E4E4E4]" />

        <form className="space-y-5" onSubmit={onSubmit} noValidate>
          {isSubmitted && (
            <div className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 font-description text-[14px] text-green-700">
              Thank you! Your message has been sent. We'll get back to you
              shortly.
            </div>
          )}
          {submitError && (
            <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 font-description text-[14px] text-red-700">
              {submitError}
            </div>
          )}

          <FormField
            name="name"
            value={form.name}
            onChange={onChange}
            label="Full Name"
            placeholder="Enter your full name"
            error={errors.name}
          />
          <FormField
            name="email"
            value={form.email}
            onChange={onChange}
            label="Email Address"
            type="email"
            placeholder="Enter your email address"
            error={errors.email}
          />
          <PhoneField
            label="Phone (optional)"
            countryCode={countryCode}
            phoneNumber={phoneNumber}
            onCountryCodeChange={setCountryCode}
            onPhoneNumberChange={setPhoneNumber}
          />
          <SelectField
            name="subject"
            label="Subject"
            value={form.subject}
            options={SUBJECT_OPTIONS}
            placeholder="Select a subject"
            onChange={onChange}
            error={errors.subject}
          />
          <FormField
            name="message"
            value={form.message}
            onChange={onChange}
            label="Message"
            placeholder="Tell us about your project, question, or how we can help..."
            multiline
            error={errors.message}
          />

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={onConsentChange}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[#E4E4E4] accent-[#2776EA]"
              />
              <span className="font-description text-[13px] font-light leading-[1.5] text-[#616161]">
                I have read and agree to the{" "}
                <a
                  href="/privacy-policy"
                  className="text-[#2776EA] underline hover:text-[#1a5cc7]"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            {consentError && (
              <p className="mt-1 ml-7 font-description text-[12px] text-red-500">
                {consentError}
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="rounded-[8px] bg-[#2776EA] px-8 py-2.5 font-title text-[14px] font-medium text-white transition-colors hover:bg-[#1a5cc7] disabled:cursor-not-allowed disabled:bg-[#E4E4E4]"
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
